// Read layer for the embeddable widget.
//
// Three modes, same return shapes so App.tsx is agnostic:
//
//  • Identified mode (the host named the reader — setTokenProvider, or the
//    older setAuthToken push) — POSTs
//    the share token AND the reader's host access token to the KB app's
//    /api/embed/articles | /api/embed/article. The server verifies that token
//    against the realm's JWKS and passes the entitlement roles it carries to
//    the same resolvers below, so the reader sees their own articles and not
//    their colleagues'. Roles cannot be derived here: a page that asserts its
//    own roles asserts anyone's.
//
//  • Token mode (data-token present) — calls the SECURITY DEFINER RPCs
//    resolve_app_share_articles / resolve_app_share_article via PostgREST. A
//    valid, unrevoked, unexpired per-app token unlocks that app's ENTIRE
//    published set (plus app-independent globals). The anon key is still sent as
//    the PostgREST apikey (the gateway credential); the token is a function
//    argument that does the real authorization.
//
//  • Anonymous mode (no token) — the original path: reads the `articles` table
//    directly with the anon key, limited by RLS to published + no_auth rows
//    scoped to the app. Kept for backward compatibility.
//
// Either way there is no supabase-js, no session; Supabase REST is CORS-enabled
// so it works cross-origin from a partner's domain.

import { identityExpected, resolveAuthToken } from "./viewer";

export type WidgetBrand = "validaide" | "skycell";
export type WidgetSide = "left" | "right";

export type WidgetConfig = {
  /** Supabase project URL, e.g. https://supabase.dev.skymind.com */
  kbUrl: string;
  /** Public anon key (safe to ship in page source — RLS is the boundary). */
  anonKey: string;
  /** APPS id to scope to, e.g. "validaide-control-intervention". */
  app: string;
  /** Per-app share token. When set, unlocks the app's full published set. */
  token?: string;
  /** Accent palette. Default "validaide". */
  brand: WidgetBrand;
  /**
   * Render the floating launcher button. Default true; hosts that open the
   * widget from their own UI (data-launcher="none") set this to false and use
   * window.SkyCellKB.open()/toggle() instead.
   */
  launcher: boolean;
  /**
   * Launcher button label. Deliberately separate from `title`: the button sits
   * in the host's chrome and reads as that host's help affordance ("Help"),
   * while the panel header names the thing it opened ("Knowledge Base").
   */
  launcherLabel: string;
  /** Panel header / dialog aria-label. */
  title: string;
  /** Which edge the launcher and panel dock to. Default "right". */
  side: WidgetSide;
  /**
   * Endpoint for support requests (POST /api/support/embed), derived from this
   * script's own origin exactly like `askUrl`. The footer's "Contact support"
   * form posts the message and its screenshots here; the server emails them on.
   * Needs `token` too — the share token is the authorization.
   *
   * Undefined — and the footer then offers no contact form at all — unless the
   * embed's app is support-enabled (see supportEnabledFor) and the host has not
   * opted out with data-support="none". Contact Support is Validaide product
   * support, not a KB-wide affordance.
   *
   * Replaces the old `data-support-url` link out to the Zendesk help centre:
   * the reader now writes the request in the panel, and can attach screenshots,
   * which a link-out (or a mailto:) could never do.
   */
  supportUrl?: string;
  /**
   * Base URL of the standalone Knowledge Base app, used for the footer's
   * "Open in the Knowledge Base" link — the same article, outside the host
   * product, where it can be bookmarked, printed and shared.
   *
   * Derived from this script's own src (the widget is served by the KB app, so
   * it already knows where the app lives) and overridable with
   * data-standalone-url for deployments with a dedicated per-app help host
   * (e.g. https://intervention.validaide.com/help). Always ends in "/".
   *
   * For a Validaide embed this default is WRONG — it points at the SkyCell
   * host, whose reader wants a Keycloak account their users do not have — so
   * App.tsx prefers the `validaideReaderBase` the server reports, unless
   * data-standalone-url was set explicitly (`standaloneUrlExplicit`).
   */
  standaloneUrl?: string;
  /** Whether `standaloneUrl` came from data-standalone-url rather than from
   *  the derived default. An explicit value always wins. */
  standaloneUrlExplicit: boolean;
  /** Context keys for the "Relevant" tab (e.g. ["cni:shipments"]). */
  context: string[];
  /**
   * "All Articles" layout: folder tiles the reader opens into (default), or
   * "none" — one flat A→Z list with no folder level at all.
   */
  folders: "tiles" | "none";
  /**
   * Which tab the panel opens on. "relevant" (default) leads with the articles
   * matched to the page the reader is on; "all" opens on the full list.
   *
   * An embed whose relevance is not yet tuned is better off leading with
   * everything than with a confident-looking list of the wrong five (asked for
   * by Validaide, 2026-09-22). It is a per-embed setting rather than a new
   * default because relevance is worth having where it has been mapped.
   */
  defaultTab: "relevant" | "all";
  /** Width (px) of the expanded panel. Default 1200. */
  expandedWidth: number;
  /**
   * The KB app's own GET /api/config, derived from this script's origin exactly
   * like `askUrl`. It reports `contentEnv` — 'sandbox' on a dev deployment,
   * 'prod' on production — which the read RPCs need so the article list follows
   * the same content state as the reader and as AI answers. Undefined when it
   * can't be derived; reads then use 'sandbox', the previous behaviour.
   */
  configUrl?: string;
  /**
   * The KB app's role-aware read endpoints (POST /api/embed/articles and
   * /api/embed/article), derived from this script's origin like `askUrl`. Used
   * only once the host has supplied a reader token (see ./viewer.ts); without
   * one the reads go straight to PostgREST as they always did. Undefined when
   * the origin can't be derived — the widget then stays role-blind rather than
   * failing, which is its previous behaviour.
   */
  readUrl?: string;
  /**
   * Endpoint for AI answers (POST /api/ask/embed), derived from this script's
   * own origin — the widget is served by the KB app host, so it already knows
   * where to ask. Undefined when it can't be derived; answers then stay off and
   * the widget is search-only. Needs `token` too: retrieval is token-scoped.
   */
  askUrl?: string;
};

export type ArticleListItem = {
  id: string;
  slug: string;
  title: string;
  folder: string;
  tags: string[];
  contextKeys: string[];
  updatedAt: string;
};

export type ArticleFull = ArticleListItem & {
  content: string;
};

/** Env-tagged reader view (sandbox rows UNION the released snapshot), so an
 *  anonymous read can ask for one content state. Mirrors READER_ARTICLE_VIEW in
 *  @skycell-ag/kb-core, which the KB app's own reader uses. */
const READER_VIEW = "articles_reader";

function base(cfg: WidgetConfig): string {
  return cfg.kbUrl.replace(/\/+$/, "");
}

function restUrl(cfg: WidgetConfig, path: string, params: string): string {
  return `${base(cfg)}/rest/v1/${path}?${params}`;
}

function rpcUrl(cfg: WidgetConfig, fn: string): string {
  return `${base(cfg)}/rest/v1/rpc/${fn}`;
}

function headers(cfg: WidgetConfig, json = false): HeadersInit {
  const h: Record<string, string> = {
    apikey: cfg.anonKey,
    Authorization: `Bearer ${cfg.anonKey}`,
    Accept: "application/json",
  };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

/** Normalize a PostgREST row (from either mode) into an ArticleListItem. */
function toListItem(r: Partial<ArticleListItem>): ArticleListItem {
  return {
    id: r.id ?? "",
    slug: r.slug ?? "",
    title: r.title ?? "Untitled",
    folder: r.folder ?? "",
    tags: Array.isArray(r.tags) ? r.tags : [],
    contextKeys: Array.isArray(r.contextKeys) ? r.contextKeys : [],
    updatedAt: r.updatedAt ?? "",
  };
}

// PostgREST returns snake_case columns; map the two we renamed.
type Row = Record<string, unknown> & {
  context_keys?: unknown;
  updated_at?: unknown;
};
function fromRow(row: Row): Partial<ArticleListItem & { content: string }> {
  return {
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    folder: row.folder as string,
    tags: row.tags as string[],
    contextKeys: (row.context_keys as string[]) ?? [],
    updatedAt: (row.updated_at as string) ?? "",
    content: row.content as string | undefined,
  };
}

/**
 * Which content state this deployment serves, from the KB app's /api/config.
 *
 * Resolved once per config and reused: it is fixed for the life of a deployment,
 * so re-asking per read would be pure latency. Keyed by the config object rather
 * than a module-level variable so two widgets (or two tests) can't leak one
 * another's answer. Any failure — no endpoint, offline, malformed — answers
 * 'sandbox', which is what these reads did before they were env-aware.
 */
export type AppConfig = {
  env: "sandbox" | "prod";
  /** Where a Validaide reader's article links belong, when this deployment
   *  answers on a Validaide host. Null otherwise. */
  validaideReaderBase: string | null;
};

const configByWidget = new WeakMap<WidgetConfig, Promise<AppConfig>>();

/** The KB app's /api/config, fetched once per widget config. Any failure —
 *  no endpoint, offline, malformed — answers the pre-config defaults. */
export function appConfig(cfg: WidgetConfig): Promise<AppConfig> {
  let pending = configByWidget.get(cfg);
  if (!pending) {
    pending = (async () => {
      const fallback: AppConfig = { env: "sandbox", validaideReaderBase: null };
      if (!cfg.configUrl) return fallback;
      try {
        const res = await fetch(cfg.configUrl, { credentials: "omit" });
        if (!res.ok) return fallback;
        const body = (await res.json()) as {
          contentEnv?: string;
          validaideReaderBase?: string | null;
        };
        return {
          env: body.contentEnv === "prod" ? "prod" : "sandbox",
          validaideReaderBase:
            typeof body.validaideReaderBase === "string" && body.validaideReaderBase
              ? body.validaideReaderBase
              : null,
        };
      } catch {
        return fallback;
      }
    })();
    configByWidget.set(cfg, pending);
  }
  return pending;
}

function contentEnv(cfg: WidgetConfig): Promise<"sandbox" | "prod"> {
  return appConfig(cfg).then((c) => c.env);
}

// Anonymous mode scope: published + no_auth, belonging to THIS app or
// app-independent (`is_global` — written once, shown for every app).
//
// Note it is `is_global`, not `app_id IS NULL`: an article with no app assigned
// is not part of any app's guest audience, so it must not surface in a
// partner's embed just because nobody has filed it yet. That is the same rule
// the `to anon` RLS policies now enforce server-side (see the guest-URL block
// in supabase/schema.sql), so this filter only narrows the request — it isn't
// what makes the boundary hold.
function scopeParams(cfg: WidgetConfig, env: "sandbox" | "prod"): string {
  const app = encodeURIComponent(cfg.app);
  return [
    `env=eq.${env}`,
    "published=eq.true",
    "no_auth=eq.true",
    `or=(app_id.eq.${app},is_global.is.true)`,
  ].join("&");
}

// PostgREST answers 503 while its schema cache is reloading — which `npm run
// db:migrate` triggers on every deploy via `notify pgrst, 'reload schema'` — and
// 520 when Kong has no healthy upstream yet. Both are transient and both are
// what supabase-js retries (postgrest-js RETRYABLE_STATUS_CODES); we talk to
// PostgREST with raw fetch, so we have to do it ourselves or a routine deploy
// shows readers a hard error.
const RETRYABLE = new Set([503, 520]);
const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 300;
const MAX_DELAY_MS = 2_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Honour `Retry-After` (seconds) when PostgREST sends it, else exponential
 *  backoff with jitter so concurrent widgets don't retry in lockstep. */
function retryDelay(res: Response, attempt: number): number {
  const header = Number(res.headers.get("Retry-After"));
  if (Number.isFinite(header) && header > 0) return Math.min(header * 1000, MAX_DELAY_MS);
  const backoff = BASE_DELAY_MS * 2 ** (attempt - 1);
  return Math.min(backoff, MAX_DELAY_MS) * (0.5 + Math.random() / 2);
}

/**
 * The reader-facing half of a failed request. The technical status goes to the
 * console for us; the thrown message is what a partner's end user actually
 * reads, so it never carries a status code. `what` names the operation in the
 * log only.
 */
function requestFailed(what: string, url: string, status: number): Error {
  console.warn(`[SkyCellKB] ${what} failed: HTTP ${status} (${url})`);
  return new Error(
    RETRYABLE.has(status) || status >= 500
      ? "The knowledge base is temporarily unavailable. Please try again in a moment."
      : "The knowledge base couldn't be reached.",
  );
}

/** fetch + retry on the transient statuses above. Returns the first response
 *  that isn't retryable (ok or not); callers still check `res.ok`. */
async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  let res: Response | undefined;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    res = await fetch(url, init);
    if (!RETRYABLE.has(res.status) || attempt === MAX_ATTEMPTS) return res;
    await sleep(retryDelay(res, attempt));
  }
  return res as Response;
}

async function rpc<T>(cfg: WidgetConfig, fn: string, args: Record<string, unknown>): Promise<T> {
  const url = rpcUrl(cfg, fn);
  const res = await fetchWithRetry(url, {
    method: "POST",
    headers: headers(cfg, true),
    credentials: "omit",
    body: JSON.stringify(args),
  });
  if (!res.ok) throw requestFailed(fn, url, res.status);
  return (await res.json()) as T;
}

/**
 * How this read should be scoped, asking the host for a current token first
 * (see ./viewer.ts — with a provider registered that is a fresh one, obtained
 * immediately before the request rather than whenever the host last pushed).
 *
 * Identified needs all three: somewhere to send it, a share token to scope
 * with, and a reader. A partial setup reads anonymously, which is what the
 * widget did before this path existed.
 *
 * Once identified, always identified. A provider that later answers with
 * nothing — expired session, a refresh that failed — sends an empty token and
 * earns a 401, which the panel renders as "reload the page". It must NOT fall
 * back to the anonymous path: with a share token that path is wider, unlocking
 * the app's entire published set. Failing closed is the point.
 */
async function identify(cfg: WidgetConfig): Promise<{ identified: boolean; token: string }> {
  if (!cfg.readUrl || !cfg.token) return { identified: false, token: "" };
  const reader = await resolveAuthToken();
  return { identified: Boolean(reader) || identityExpected(), token: reader ?? "" };
}

/** A failed identified read, carrying the status so the caller can tell "not in
 *  your scope" (404 — an ordinary outcome) from a real failure, without
 *  matching on the reader-facing message. */
class ReadError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/**
 * POST to the KB app's role-aware read endpoint. Errors are surfaced with the
 * same reader-facing wording as the PostgREST path; 401 means the host's token
 * has expired, which the panel answers by asking the reader to reload so the
 * host mints a fresh one.
 */
async function serverRead<T>(
  url: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    throw new ReadError(401, "Your session has expired. Please reload the page.");
  }
  if (!res.ok) throw new ReadError(res.status, requestFailed("identified read", url, res.status).message);
  return (await res.json()) as T;
}

/** List articles for the app (no heavy `content`). */
export async function listArticles(cfg: WidgetConfig): Promise<ArticleListItem[]> {
  const env = await contentEnv(cfg);
  const reader = await identify(cfg);
  if (reader.identified) {
    const body = await serverRead<{ articles?: Row[] }>(`${cfg.readUrl}articles`, {
      token: cfg.token,
      authToken: reader.token,
    });
    return (body.articles ?? []).map((row) => toListItem(fromRow(row)));
  }
  if (cfg.token) {
    const rows = await rpc<Row[]>(cfg, "resolve_app_share_articles", {
      p_token: cfg.token,
      p_env: env,
    });
    return rows.map((row) => toListItem(fromRow(row)));
  }
  const params = [
    "select=id,slug,title,folder,tags,context_keys,updated_at",
    scopeParams(cfg, env),
    "order=folder.asc,title.asc",
  ].join("&");
  const url = restUrl(cfg, READER_VIEW, params);
  const res = await fetchWithRetry(url, {
    headers: headers(cfg),
    credentials: "omit",
  });
  if (!res.ok) throw requestFailed("article list", url, res.status);
  const rows = (await res.json()) as Row[];
  return rows.map((row) => toListItem(fromRow(row)));
}

/** Fetch one article's full content by slug. */
export async function getArticle(
  cfg: WidgetConfig,
  slug: string,
): Promise<ArticleFull | null> {
  let row: Partial<ArticleFull> | undefined;
  const env = await contentEnv(cfg);
  const reader = await identify(cfg);
  if (reader.identified) {
    try {
      const body = await serverRead<Row>(`${cfg.readUrl}article`, {
        token: cfg.token,
        slug,
        authToken: reader.token,
      });
      row = fromRow(body);
    } catch (e) {
      // 404 is "not in your scope", which the panel renders as "that article
      // isn't available here" — the same as an empty PostgREST result, and the
      // same answer for a slug that exists behind a role the reader lacks.
      // Everything else is a real failure and keeps its message.
      if (e instanceof ReadError && e.status === 404) row = undefined;
      else throw e;
    }
  } else if (cfg.token) {
    const rows = await rpc<Row[]>(cfg, "resolve_app_share_article", {
      p_token: cfg.token,
      p_slug: slug,
      p_env: env,
    });
    row = rows[0] ? fromRow(rows[0]) : undefined;
  } else {
    const params = [
      "select=id,slug,title,folder,tags,context_keys,updated_at,content",
      scopeParams(cfg, env),
      `slug=eq.${encodeURIComponent(slug)}`,
      "limit=1",
    ].join("&");
    const url = restUrl(cfg, READER_VIEW, params);
    const res = await fetchWithRetry(url, {
      headers: headers(cfg),
      credentials: "omit",
    });
    if (!res.ok) throw requestFailed("article fetch", url, res.status);
    const rows = (await res.json()) as Row[];
    row = rows[0] ? fromRow(rows[0]) : undefined;
  }
  if (!row) return null;
  return { ...toListItem(row), content: row.content ?? "" };
}

/**
 * Record a "Was this article helpful?" vote.
 *
 * Writes straight to `article_feedback` — the same table and the same anonymous
 * `anyone inserts` RLS policy the KB's own reader uses (ArticleEngagement.tsx),
 * so widget votes land in the existing editor analytics rather than a parallel
 * store. Reads stay staff-only. Best-effort: a failed vote is never surfaced to
 * the reader, who has already been thanked.
 */
export async function submitFeedback(
  cfg: WidgetConfig,
  articleId: string,
  helpful: boolean,
): Promise<void> {
  await fetch(restUrl(cfg, "article_feedback", ""), {
    method: "POST",
    headers: { ...(headers(cfg, true) as Record<string, string>), Prefer: "return=minimal" },
    credentials: "omit",
    body: JSON.stringify({ article_id: articleId, helpful }),
  });
}

/**
 * Apps whose readers belong on the Validaide reader host (`/v/`, signed in
 * through Validaide's own `/kb/authorize`). That handshake is scoped to one
 * app — the one KB_VALIDAIDE_SHARE_TOKEN shares — so sending any other app's
 * reader there (e.g. Control & Intervention, whose users have SkyCell
 * accounts) lands them on Validaide's "Forbidden" page. Those keep the
 * derived default: the KB host serving this bundle.
 */
const VALIDAIDE_READER_APPS = ["validaide"];

/** Whether `app` (a `data-app` value) should link to the Validaide reader. */
export const validaideReaderFor = (app: string | undefined): boolean =>
  VALIDAIDE_READER_APPS.includes((app ?? "").trim());

// --- support requests --------------------------------------------------------

/**
 * Apps whose embed may offer the in-panel contact form.
 *
 * Contact Support is Validaide product support — the form mails an inbox the
 * Validaide team staffs — so only the `validaide` embed offers it. The other
 * apps (Asset Tracking, Control & Intervention, Booking Portal) take help
 * requests through the Jira service-desk widget in the host product, and a
 * second, unrelated inbox in the KB panel would mis-route them.
 *
 * Mirrors SUPPORT_APPS in src/lib/types.ts, which the server enforces on POST
 * /api/support/embed. Duplicated rather than imported because the widget is a
 * standalone bundle that imports nothing from src/.
 */
const SUPPORT_APPS = ["validaide"];

/** Whether `app` (a `data-app` value) may offer the in-panel contact form. */
export const supportEnabledFor = (app: string | undefined): boolean =>
  SUPPORT_APPS.includes((app ?? "").trim());

/** What the reader typed into the footer's contact form, plus where they were. */
export type SupportRequest = {
  /** Reply-to address. */
  email: string;
  subject: string;
  message: string;
  /** Screenshots, as picked or pasted. */
  screenshots: File[];
  /** Whether they ticked "Send me a copy" — opt-in, so absent means no. */
  ccMe: boolean;
  /** The article open when they asked for help, if any. */
  articleSlug?: string;
  articleTitle?: string;
};

/**
 * Whether the server can actually deliver a support request (it needs SMTP
 * configured). Asked once when the panel opens so the form is never offered
 * when submitting it could only fail. Any error answers "no".
 */
export async function supportAvailable(cfg: WidgetConfig): Promise<boolean> {
  if (!cfg.supportUrl || !cfg.token) return false;
  try {
    const res = await fetch(cfg.supportUrl, { method: "GET" });
    if (!res.ok) return false;
    const body = (await res.json()) as { enabled?: boolean };
    return body.enabled === true;
  } catch {
    return false;
  }
}

/**
 * Send a support request. multipart/form-data, because the screenshots are the
 * point — that is what the old link-out to the Zendesk form did and a mailto:
 * cannot.
 *
 * Throws with a reader-facing message; the form shows it inline and keeps
 * everything the reader typed so they can retry without re-writing it.
 */
export async function submitSupportRequest(
  cfg: WidgetConfig,
  req: SupportRequest,
): Promise<void> {
  if (!cfg.supportUrl || !cfg.token) {
    throw new Error("Support requests aren't available here.");
  }
  const form = new FormData();
  form.set("token", cfg.token);
  form.set("email", req.email);
  form.set("subject", req.subject);
  form.set("message", req.message);
  // Only when ticked: the server treats anything else as unchecked, but sending
  // the field only when true keeps the wire honest about what was asked for.
  if (req.ccMe) form.set("ccMe", "true");
  // Where they were. The host page URL is the single most useful line on the
  // resulting ticket, and it saves support a round-trip asking for it.
  form.set("pageUrl", window.location.href);
  if (req.articleSlug) form.set("articleSlug", req.articleSlug);
  if (req.articleTitle) form.set("articleTitle", req.articleTitle);
  for (const file of req.screenshots) form.append("screenshots", file, file.name);

  let res: Response;
  try {
    res = await fetch(cfg.supportUrl, { method: "POST", body: form });
  } catch {
    throw new Error("Couldn't reach support. Check your connection and try again.");
  }
  if (!res.ok) {
    // The server's messages are already written for a reader; fall back to a
    // generic line for the statuses that carry no body (413 from a proxy, say).
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || "Couldn't send your request. Please try again.");
  }
}

// Apps whose Relevant tab would guess rather than know: nobody has mapped
// their articles to host pages yet, so a full list beats a confident-looking
// list of the wrong five. Remove an app once its context keys are in.
const ALL_BY_DEFAULT = new Set(["validaide"]);

/** data-default-tab. An explicit value always wins. */
export function parseDefaultTab(raw: string | undefined, app: string): "relevant" | "all" {
  const v = raw?.trim();
  if (v === "all" || v === "relevant") return v;
  return ALL_BY_DEFAULT.has(app) ? "all" : "relevant";
}
